import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AgreementService } from '../agreement.service';
import { AppTextService } from '@fi-shared/app-text';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { MoreMenuMobileSettingsModel } from '@fi-app/utilities/more-menu-mobile/more-menu-mobile-settings.model';
import { FiPrintDownloadComponent } from '@fi-shared/fi-print-download/fi-print-download.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { AgreementModel } from '@fi-shared/fi-agreement/agreement.model';
import { isIOS } from '@fi-shared/utils/isIOS';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { ITabItem } from '@fi-app/utilities/tabs/tabs';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';

@Component({
    selector: 'fi-agreement-view',
    templateUrl: './agreement-view.component.html',
    styleUrls: ['./agreement-view.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-agreement-view' },
    standalone: false,
})
export class FiAgreementViewComponent implements OnInit {
    @Input() agreement: AgreementModel;

    tabs: ITabItem[] = [];
    EditTabs: EditTabsModel;
    moreMenuItems: MoreMenuMobileSettingsModel;
    private allowEdit: boolean;
    SelectedTabIndex = 0;

    @ViewChild(FiPrintDownloadComponent, { static: false }) printDownloadComponent: FiPrintDownloadComponent;
    GetAgreementHtml: () => any;
    FiStateNames = FiStateNames;

    constructor(
        private agreementService: AgreementService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private loadingService: LoadingService,
        private stateAccessCheckService: StateAccessCheckService,
        private stateService: FiStateService,
        private detectDevicesService: DetectDevicesService,
    ) {}

    ngOnInit() {
        this.setPageHeaderData();
        this.setTabsData();
        this.setInitialTabIndex();
    }

    isRightAlignButtonContainerShown() {
        return !this.isMobilePhone() && this.isConfidentialAgreement() && this.allowEdit;
    }

    private setPageHeaderData() {
        this.EditTabs = new EditTabsModel(this.getAgreementName(), this.appTextService.get('back'));
        this.moreMenuItems = new MoreMenuMobileSettingsModel();
        this.allowEdit = this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Agreement.Edit);

        if (!isIOS()) {
            this.moreMenuItems.AddListItem(this.appTextService.get('common.print'), () =>
                this.printDownloadComponent.Print(),
            );
        }

        this.moreMenuItems.AddListItem(this.appTextService.get('common.download'), () =>
            this.printDownloadComponent.Download(),
        );

        if (this.isConfidentialAgreement() && this.allowEdit) {
            this.moreMenuItems.AddListItem(this.appTextService.get('agreement.republish'), () => this.rePublish());
            this.moreMenuItems.AddListItem(this.appTextService.get('common.edit'), () => this.editAgreement());
        }
        this.GetAgreementHtml = () => {
            if (!this.isConfidentialAgreement()) {
                const publishedContent = this.agreement.GetPublishedContentByContentLanguage(
                    this.agreement.selectedLanguage,
                );
                return publishedContent ? publishedContent.Content : '';
            }

            return '';
        };
    }

    private setTabsData() {
        this.tabs.push({
            Title: this.appTextService.get('common.agreement'),
            Sref: FiStateNames.Agreement.View._name,
        });

        if (this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Agreement.View.Accepted.List)) {
            this.tabs.push({
                Title: this.appTextService.get('common.accepted'),
                Sref: FiStateNames.Agreement.View.Accepted.List,
            });
        }
    }

    private setInitialTabIndex() {
        this.SelectedTabIndex = this.stateService.current.name === FiStateNames.Agreement.View._name ? 0 : 1;
    }

    private isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    getAgreementFullName(): string {
        return `${this.getAgreementName()} ${this.agreement.ChangedOnDate}`;
    }

    isConfidentialAgreement(): boolean {
        return this.agreement.AgreementType === this.agreementService.TypeConfidential;
    }

    rePublish() {
        this.fiDialogsService
            .confirm({
                headerText: this.appTextService.get('dialog.republish_confirm_header'),
                messageText: this.appTextService.get('agreement.republish_confirm_message'),
                yesButtonText: this.appTextService.get('common.confirm'),
            })
            .onYesButtonClick(() => {
                this.loadingService.trackPromise(this.agreementService.RepublishAgreement(this.agreement)).then(
                    () => {
                        this.fiDialogsService.notify({
                            headerText: this.appTextService.get('common.information'),
                            messageText: this.appTextService.get('agreement.republished_text'),
                        });
                    },
                    () => {
                        this.fiDialogsService.error();
                    },
                );
            });
    }

    editAgreement() {
        this.stateService.go(FiStateNames.Agreement.Edit, {
            Id: this.agreement.Id,
            languageId: this.getLanguageId(),
        });
    }

    onTabSelect(index) {
        this.stateService.go(this.tabs[index].Sref, undefined, { location: 'replace' });
    }

    isShowPrintDownload(): boolean {
        return this.stateService.current.name === FiStateNames.Agreement.View._name;
    }

    getLanguageId(): number {
        return this.stateService.params.languageId;
    }

    getAgreementName() {
        return this.agreementService.getAgreementName(this.agreement);
    }
}
